import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Heart, ShoppingBasket, AlertTriangle, Clock, ChefHat, User, Loader2, ImageIcon, Crown } from 'lucide-react';
import { findRecipe, getRecipeDetail } from '../data/mock';
import { useKit } from '../hooks/useKit';
import { API, useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

export default function RecipeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isSaved, toggle } = useKit();
  const { user } = useAuth();
  const [photo, setPhoto] = useState(null);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    let active = true;
    setPhoto(null);
    setPhotoLoading(true);
    const load = async () => {
      try {
        const cached = await axios.get(`${API}/illustration/${encodeURIComponent(id)}`);
        if (active) { setPhoto(cached.data.image); setPhotoLoading(false); }
        return;
      } catch (e) { /* generate */ }
      const r = findRecipe(id);
      if (!r) { if (active) setPhotoLoading(false); return; }
      try {
        const res = await axios.post(`${API}/illustration`, { item_id: r.id, name: r.name, latin: '', category: r.type, kind: 'recipe' });
        if (active) setPhoto(res.data.image);
      } catch (e) { /* placeholder */ }
      finally { if (active) setPhotoLoading(false); }
    };
    load();
    return () => { active = false; };
  }, [id]);

  const recipe = findRecipe(id);
  if (!recipe) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-20 text-center min-h-[60vh]">
        <p className="text-botanica-muted mb-4">We couldn't find that recipe.</p>
        <Link to="/recipes" className="text-botanica-green underline">Back to recipes</Link>
      </div>
    );
  }

  const detail = getRecipeDetail(recipe);
  const saved = isSaved(recipe.id);
  const profile = recipe.gender === 'all' ? 'all' : recipe.gender;

  const handleSave = () => {
    const added = toggle({ id: recipe.id, name: recipe.name, type: 'recipe', subtitle: `${recipe.type} · ${recipe.time}`, ingredients: detail.ingredients });
    toast[added ? 'success' : 'message'](added ? `${recipe.name} added to your kit` : `${recipe.name} removed from your kit`);
  };

  const addToShopping = async () => {
    const isPremium = user?.is_premium;
    if (!isPremium) {
      toast.message('Shopping List is a Premium feature', {
        description: 'Upgrade to send ingredients to your synced shopping list.',
        action: { label: 'Upgrade', onClick: () => navigate('/membership') },
      });
      return;
    }
    setAdding(true);
    try {
      const res = await axios.post(`${API}/shopping-list/add-recipe`, {
        recipe_id: recipe.id,
        recipe_name: recipe.name,
        ingredients: detail.ingredients,
      });
      const added = res.data?.added ?? 0;
      toast.success(added > 0 ? `${added} ingredient${added > 1 ? 's' : ''} added to your shopping list.` : 'Already on your shopping list.', {
        action: { label: 'View list', onClick: () => navigate('/shopping-list') },
      });
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.message('Shopping List is a Premium feature', {
          description: 'Upgrade to send ingredients to your synced shopping list.',
          action: { label: 'Upgrade', onClick: () => navigate('/membership') },
        });
      } else {
        toast.error('Could not add to your shopping list. Please try again.');
      }
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-5 py-6 min-h-[70vh]">
      <div className="flex items-center justify-between mb-6">
        <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-full bg-botanica-card/70 flex items-center justify-center text-botanica-ink hover:bg-botanica-card transition-colors">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <button
          onClick={handleSave}
          className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${saved ? 'bg-botanica-green text-white' : 'bg-botanica-card/70 text-botanica-muted hover:text-botanica-ink'}`}
        >
          <Heart className={`w-4 h-4 ${saved ? 'fill-white' : ''}`} />
        </button>
      </div>

      {/* Permanent photo */}
      <div className="w-full rounded-3xl overflow-hidden bg-botanica-card/70 border border-botanica-card mb-6">
        {photo ? (
          <img src={photo} alt={`Photo of ${recipe.name}`} className="w-full h-56 md:h-72 object-cover" />
        ) : photoLoading ? (
          <div className="h-56 md:h-72 flex flex-col items-center justify-center gap-2 animate-pulse">
            <Loader2 className="w-7 h-7 text-botanica-green animate-spin" />
            <span className="text-sm text-botanica-muted">Loading photo…</span>
          </div>
        ) : (
          <div className="h-56 md:h-72 flex flex-col items-center justify-center gap-2">
            <ImageIcon className="w-7 h-7 text-botanica-muted" />
            <span className="text-sm text-botanica-muted">Photo unavailable</span>
          </div>
        )}
      </div>

      <p className="text-[11px] uppercase tracking-widest text-botanica-green font-semibold mb-1">{recipe.type}</p>
      <h1 className="font-serif text-4xl text-botanica-ink mb-2">{recipe.name}</h1>
      <div className="flex flex-wrap items-center gap-3 text-sm text-botanica-muted mb-3">
        <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {recipe.time}</span>
        <span className="flex items-center gap-1"><ChefHat className="w-4 h-4" /> {recipe.difficulty}</span>
        <span className="flex items-center gap-1"><User className="w-4 h-4" /> for {profile} ({recipe.ages[0]})</span>
      </div>
      <div className="flex flex-wrap gap-2 mb-6">
        {recipe.symptoms.map((s) => (
          <span key={s} className="text-xs px-3 py-1 rounded-full bg-botanica-card text-botanica-ink">{s}</span>
        ))}
      </div>

      <button
        onClick={addToShopping}
        disabled={adding}
        className="w-full flex items-center justify-center gap-2 py-3.5 rounded-full bg-botanica-green text-white font-medium hover:bg-botanica-greenDark transition-colors mb-8 disabled:opacity-70"
        data-testid="add-to-shopping-btn"
      >
        {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : (user?.is_premium ? <ShoppingBasket className="w-4 h-4" /> : <Crown className="w-4 h-4" />)}
        {user?.is_premium ? 'Add ingredients to shopping list' : 'Add to shopping list (Premium)'}
      </button>

      <section className="mb-8">
        <h2 className="font-serif text-2xl text-botanica-ink mb-3">Ingredients</h2>
        <ul className="space-y-2">
          {detail.ingredients.map((i) => (
            <li key={i} className="flex items-start gap-2.5 text-sm text-botanica-ink">
              <span className="w-1.5 h-1.5 rounded-full bg-botanica-green mt-2 shrink-0" /> {i}
            </li>
          ))}
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="font-serif text-2xl text-botanica-ink mb-3">Instructions</h2>
        <ol className="space-y-3">
          {detail.steps.map((s, idx) => (
            <li key={idx} className="flex items-start gap-3 text-sm text-botanica-ink">
              <span className="w-6 h-6 rounded-full bg-botanica-greenLight text-botanica-greenDark text-xs flex items-center justify-center shrink-0 mt-0.5 font-medium">{idx + 1}</span>
              {s}
            </li>
          ))}
        </ol>
      </section>

      <div className="rounded-2xl border-l-4 border-l-botanica-warn bg-amber-50 px-5 py-4">
        <p className="text-sm font-semibold text-botanica-warn mb-1 flex items-center gap-1.5">
          <AlertTriangle className="w-4 h-4" /> Safety
        </p>
        <p className="text-sm text-botanica-warn">{detail.safety}</p>
      </div>

      <button
        onClick={handleSave}
        className={`w-full mt-8 flex items-center justify-center gap-2 py-3.5 rounded-full text-sm font-medium transition-colors ${saved ? 'bg-botanica-card text-botanica-ink' : 'bg-botanica-green text-white hover:bg-botanica-greenDark'}`}
      >
        <Heart className={`w-4 h-4 ${saved ? 'fill-botanica-green text-botanica-green' : ''}`} />
        {saved ? 'Saved to your kit' : 'Save recipe to my kit'}
      </button>
    </div>
  );
}
